'use client';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useCollection, useFirestore, useMemoFirebase } from '@/firebase';
import { collection, query, where } from 'firebase/firestore';
import { type Review } from '@/models/review';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import { Button } from './ui/button';

function ReviewCard({ review }: { review: Review }) {
  const averageScore = Math.round((review.scoreQuality + review.scoreHandling + review.scoreService) / 3);
  const createdAtDate = review.createdAt?.toDate ? review.createdAt.toDate() : new Date();

  return (
    <Card className="bg-background">
      <CardHeader className="flex-row items-start gap-4 space-y-0 pb-3">
        <Avatar>
          <AvatarFallback>{review.authorName.charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
            <div className="flex items-center gap-2">
                <p className="font-semibold">{review.authorName}</p>
                {!review.isAnonymous && (
                    <div className="flex items-center" title="Geverifieerde recensie">
                        <CheckBadgeIcon className="h-5 w-5 text-primary" />
                    </div>
                )}
            </div>
          <p className="text-xs text-muted-foreground">{createdAtDate.toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold">{averageScore.toFixed(1)}</span>
          <span className="text-sm text-muted-foreground">/10</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-muted-foreground ">{review.text}</p>
        <div className="flex flex-wrap gap-2 text-xs pt-3 border-t">
            <Badge variant="outline">Kwaliteit: <span className="font-bold ml-1.5">{review.scoreQuality}/10</span></Badge>
            <Badge variant="outline">Omgang: <span className="font-bold ml-1.5">{review.scoreHandling}/10</span></Badge>
            <Badge variant="outline">Service: <span className="font-bold ml-1.5">{review.scoreService}/10</span></Badge>
        </div>
      </CardContent>
    </Card>
  );
}


export default function SalonReviews({ salonId, onReviewsLoaded }: { salonId: string; onReviewsLoaded: (stats: { score: number; count: number }) => void; }) {
  const firestore = useFirestore();
  const [visibleReviewCount, setVisibleReviewCount] = useState(5);

  const fiveYearsAgo = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 5);
    return date;
  }, []);

  const reviewsQuery = useMemoFirebase(() => {
    if (!firestore || !salonId) return null;
    return query(
      collection(firestore, 'reviews'),
      where('salonId', '==', salonId)
    );
  }, [firestore, salonId]);

  const { data: allSalonReviews, isLoading } = useCollection<Review>(reviewsQuery);
  
  const approvedReviews = useMemo(() => {
    if (!allSalonReviews) return [];
    return allSalonReviews
        .filter(review => {
            const createdAtDate = review.createdAt?.toDate ? review.createdAt.toDate() : new Date(0);
            return review.status === 'approved' && createdAtDate >= fiveYearsAgo;
        })
        .sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return dateB - dateA; // Sort descending
        });
  }, [allSalonReviews, fiveYearsAgo]);

  const visibleReviews = useMemo(() => approvedReviews.slice(0, visibleReviewCount), [approvedReviews, visibleReviewCount]);
  const showLoadMore = useMemo(() => approvedReviews.length > visibleReviewCount, [approvedReviews, visibleReviewCount]);

  useEffect(() => {
    const totalScore = approvedReviews.reduce((acc, review) => acc + (review.scoreQuality + review.scoreHandling + review.scoreService) / 3, 0);
    const avgScore = approvedReviews.length > 0 ? totalScore / approvedReviews.length : 0;
    onReviewsLoaded({ score: avgScore, count: approvedReviews.length });
  }, [approvedReviews, onReviewsLoaded]);

  const handleLoadMore = () => {
    setVisibleReviewCount(approvedReviews.length); // Load all remaining reviews
  };

  return (
    <Card className="shadow-xl bg-card">
      <CardHeader>
        <CardTitle>Recensies</CardTitle>
        { !isLoading && approvedReviews.length === 0 && (
            <CardDescription>Er zijn nog geen recensies voor dit salon. Wees de eerste!</CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
             <div className="space-y-4">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
            </div>
        ) : (
            visibleReviews.map(review => <ReviewCard key={review.id} review={review} />)
        )}
      </CardContent>
      {showLoadMore && (
        <CardFooter className="justify-center">
            <Button onClick={handleLoadMore}>Toon meer recensies</Button>
        </CardFooter>
      )}
    </Card>
  );
}
